import { FocusKeyManager, FocusMonitor } from '@angular/cdk/a11y';
import { AfterViewInit, ContentChildren, Directive, DoCheck, ElementRef, Input, OnDestroy, QueryList, inject } from '@angular/core';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { BehaviorSubject, fromEvent, Subject, takeUntil } from 'rxjs';
import { preventOnOrigin } from '../../../annotations/prevent-on-keyboard-origin.annotation';
import { EspMatTreeNode, EspNestedTreeNode } from './esp-tree-node.directive';
import { EspTreeUnit } from './esp-tree-unit-exstention.interface';

type EspTreeNode<T extends EspTreeUnit<T>, K = T> =
    | EspMatTreeNode<T, K>
    | EspNestedTreeNode<T, K>;

@Directive({
    selector: 'mat-tree',
    host: {
        tabIndex: '0',
        '(focus)': 'onFocus($event)',
        //   '(blur)': 'onBlur($event)',
    },
    standalone: true,
})
export class EspMatTreeDirective<T extends EspTreeUnit<T>, K = T>
    implements AfterViewInit, OnDestroy, DoCheck
{
    private el = inject<ElementRef<HTMLElement>>(ElementRef);
    private focusMonitor = inject(FocusMonitor);

    // TODO: Skipped for migration because:
    //  Class of this input is referenced in the signature of another class.
    @Input() espMatTreeChildKey = 'subUnits';
    private activeIndex: number;
    private keyManager: FocusKeyManager<EspTreeNode<T>>;

    private index = -1;
    private destroy = new Subject<void>();

    private _data: T[];
    private _nodeList: EspTreeNode<T>[];
    private currentIndex$ = new BehaviorSubject<number>(0);

    private _dataSource: MatTreeNestedDataSource<T>;

    // TODO: Skipped for migration because:
    //  Accessor inputs cannot be migrated as they are too complex.
    @Input()
    get dataSource() {
        return this._dataSource;
    }

    set dataSource(data: MatTreeNestedDataSource<T>) {
        this._dataSource = data;
    }

    private _nodes: QueryList<EspTreeNode<T>>;

    // TODO: Skipped for migration because:
    //  Accessor queries cannot be migrated as they are too complex.
    @ContentChildren('espMatNestedTreeNode,espMatTreeNode', {
        descendants: true,
    })
    get nodes() {
        return this._nodes;
    }

    set nodes(nodes: QueryList<EspTreeNode<T>>) {
        if (nodes?.length && nodes != this.nodes) {
            this._nodeList = Array.from(nodes);
            this._nodes = nodes;
        }
    }

    set currentIndex(index: number) {
        this.currentIndex$.next(index);
        this.activeIndex = index;
    }

    ngDoCheck(): void {
        if (
            this._nodeList?.length &&
            this.dataSource?.data?.length &&
            this.dataSource.data != this._data
        ) {
            this._data = this.dataSource.data;
            this.initKeyManager();
        }
    }

    ngAfterViewInit(): void {
        this.updateCurrentIndexOnChanges();
        this.manageTreeFocusOnBlur();
        this.handleKeyboardEvents();
    }

    updateNested(nested: T[]) {
        nested?.forEach((node: T) => {
            this.index++;
            if (node?.[this.espMatTreeChildKey]?.length) {
                node.index = this.index;
                return this.updateNested(node?.[this.espMatTreeChildKey]);
            } else {
                node.index = this.index;
            }
        });
    }

    @preventOnOrigin('mouse')
    onFocus(event?) {
        const active = this.activeIndex ?? 0;
        this.keyManager.setActiveItem(active);
        this.el.nativeElement.tabIndex = -1;
    }

    onBlur(event?) {
        this.activeIndex = this.keyManager?.activeItemIndex;
        this.el.nativeElement.tabIndex = 0;
    }

    ngOnDestroy(): void {
        this.destroy.next();
        this.destroy.complete();
    }

    private handleKeyboardEvents() {
        fromEvent(this.el.nativeElement, 'keydown')
            .pipe(takeUntil(this.destroy))
            .subscribe((event: KeyboardEvent) => {
                switch (event.key) {
                    case 'ArrowDown':
                        this.keyManager.setNextItemActive();
                        break;
                    case 'ArrowUp':
                        this.keyManager.setPreviousItemActive();
                        break;
                    case 'ArrowRight':
                        if (
                            this.keyManager.activeItem.nodeType ===
                            'MAT-NESTED-TREE-NODE'
                        ) {
                            this.keyManager.setNextItemActive();
                        }
                        break;
                    case 'Home':
                        this.keyManager.setFirstItemActive();
                        break;
                    case 'End':
                        this.keyManager.setLastItemActive();
                        break;
                    default:
                        break;
                }
                event.stopPropagation();
            });
    }

    private manageTreeFocusOnBlur() {
        this.focusMonitor
            .monitor(this.el, true)
            .pipe(takeUntil(this.destroy))
            .subscribe((origin) => {
                if (!origin) {
                    this.onBlur();
                }
            });
    }

    private updateCurrentIndexOnChanges() {
        this.currentIndex$.pipe(takeUntil(this.destroy)).subscribe((index) => {
            if (this.keyManager?.activeItemIndex !== index) {
                this.keyManager?.updateActiveItem(index);
            }
        });
    }

    private initKeyManager() {
        this.updateNested(this.dataSource.data);
        this._nodeList = this._nodeList.sort(
            (n1, n2) => n1.getData.index - n2.getData.index
        );
        this.keyManager = new FocusKeyManager(this._nodeList)
            .withVerticalOrientation()
            .withHomeAndEnd()
            .skipPredicate((node) => node.skip());
        this.keyManager.updateActiveItem(0);
    }
}
